import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { getAppMode } from '../appMode';
import {
  ensureSaasOwnerProfile,
  ensureTbSiteWorkspaceOrg,
  ensureWorkspaceOrg,
  fetchOrgById,
  fetchTbSiteOrganization,
  setWorkspaceOrg,
  type SaasOrg,
} from '../lib/saasOrg';
import { fetchImpersonationWorkspaceOrg } from '../lib/impersonationWorkspace';
import { setActiveWorkspaceOrgId } from '../lib/activeWorkspaceOrgId';
import { getImpersonationOverride } from '../../utils/userImpersonationOverride';

interface SaasWorkspaceContextValue {
  org: SaasOrg | null;
  orgId: string | null;
  isReady: boolean;
  /** Expired no-card trial: dashboards stay open, writes should be refused. */
  readOnly: boolean;
  refreshWorkspace: () => Promise<SaasOrg | null>;
  switchWorkspace: (orgId: string) => Promise<void>;
}

const SaasWorkspaceContext = createContext<SaasWorkspaceContextValue | null>(null);

function SaasWorkspaceProviderBody({ children }: { children: ReactNode }) {
  const auth = useOptionalAuth();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const authLoading = auth?.isLoading ?? false;
  const isImpersonating = auth?.isImpersonating ?? false;
  const realUserId = auth?.realUser?.id ?? null;
  const effectiveUserId = auth?.user?.id ?? null;
  const impersonationOrgId =
    isImpersonating && realUserId
      ? getImpersonationOverride(realUserId)?.workspaceOrgId?.trim() || null
      : null;
  const isSaas = getAppMode() === 'saas-platform';
  const [org, setOrg] = useState<SaasOrg | null>(null);
  const [ready, setReady] = useState(true);
  const hasWorkspaceRef = useRef(false);

  const refreshWorkspace = useCallback(async () => {
    if (!isSaas) {
      await ensureTbSiteWorkspaceOrg();
      const tbOrg = await fetchTbSiteOrganization();
      setOrg(tbOrg);
      if (tbOrg?.id) setActiveWorkspaceOrgId(tbOrg.id);
      return tbOrg;
    }
    if (isImpersonating) {
      const o = await fetchImpersonationWorkspaceOrg(realUserId);
      setOrg(o);
      if (o?.id) setActiveWorkspaceOrgId(o.id);
      return o;
    }
    const o = await ensureWorkspaceOrg();
    setOrg(o);
    return o;
  }, [isSaas, isImpersonating, realUserId]);

  const switchWorkspace = useCallback(async (orgId: string) => {
    await setWorkspaceOrg(orgId);
    const o = await fetchOrgById(orgId);
    setOrg(o);
  }, []);

  useEffect(() => {
    if (!isSaas) {
      if (authLoading) return;
      if (!isAuthenticated) {
        setOrg(null);
        setReady(true);
        return;
      }
      let cancelled = false;
      setReady(true);
      void (async () => {
        await ensureTbSiteWorkspaceOrg();
        const tbOrg = await fetchTbSiteOrganization();
        if (cancelled || !tbOrg) return;
        setActiveWorkspaceOrgId(tbOrg.id);
        setOrg(tbOrg);
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!isAuthenticated) {
      hasWorkspaceRef.current = false;
      setOrg(null);
      setReady(true);
      return;
    }
    let cancelled = false;
    const readyTimeout = window.setTimeout(() => {
      if (!cancelled) setReady(true);
    }, 2500);
    const resolve = isImpersonating ? fetchImpersonationWorkspaceOrg(realUserId) : ensureWorkspaceOrg();
    resolve
      .then((o) => {
        if (cancelled) return;
        // Pin the header org in the same tick as setOrg so /api list fetches
        // never race a paint where activeWorkspaceOrgId is still null.
        if (o?.id) setActiveWorkspaceOrgId(o.id);
        if (o) hasWorkspaceRef.current = true;
        setOrg(o);
        setReady(true);
        if (!isImpersonating) void ensureSaasOwnerProfile();
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      })
      .finally(() => {
        window.clearTimeout(readyTimeout);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(readyTimeout);
    };
  }, [
    isSaas,
    isAuthenticated,
    authLoading,
    isImpersonating,
    realUserId,
    effectiveUserId,
    impersonationOrgId,
  ]);

  useEffect(() => {
    if (!isSaas) {
      // toolsandbubbles.com: leave T&B workspace id set by ensureTbSiteWorkspaceOrg /
      // setWorkspaceOrg — do not clear it or staff list APIs lose the org header.
      return;
    }
    if (!isAuthenticated) {
      setActiveWorkspaceOrgId(null);
      return;
    }
    // Never clear a pin that setWorkspaceOrg already wrote (impersonation / view-as).
    // A null org resolve must not wipe X-Workspace-Org-Id mid-session.
    if (org?.id) setActiveWorkspaceOrgId(org.id);
  }, [isSaas, isAuthenticated, org?.id]);

  const value = useMemo(
    () => ({
      org,
      orgId: org?.id ?? null,
      isReady: ready,
      readOnly: org?.status === 'paused',
      refreshWorkspace,
      switchWorkspace,
    }),
    [org, ready, refreshWorkspace, switchWorkspace],
  );

  return <SaasWorkspaceContext.Provider value={value}>{children}</SaasWorkspaceContext.Provider>;
}

export function SaasWorkspaceProvider({ children }: { children: ReactNode }) {
  return <SaasWorkspaceProviderBody>{children}</SaasWorkspaceProviderBody>;
}

export function useSaasWorkspace(): SaasWorkspaceContextValue {
  const ctx = useContext(SaasWorkspaceContext);
  if (!ctx) {
    return {
      org: null,
      orgId: null,
      isReady: true,
      readOnly: false,
      refreshWorkspace: async () => null,
      switchWorkspace: async () => {},
    };
  }
  return ctx;
}
